import React, { useState } from 'react'
import to from 'await-catch'
import styled from 'styled-components'
import { Button } from '../../../../components/ui/button'
import Feedback from '../../../../components/feedback'
import useIsMobile from '../../../../hooks/useIsMobile'
import { Calendar } from '../../../../assets/icons'
import theme from '../../../../styled/theme'
import moment from 'moment'
import config from '../../../../config'
import { formatDetailText } from '../../../../utils/stringsUtils'
import { formatAppointmentDate } from '../../../../utils/formatDate'
import AddToCalendarQuickAccessModal from '../../../../components/AddToCalendarQuickAccessModal'
import CancelAppointmentConfirmModal from '../../../../components/CancelAppointmentConfirmModal'
import { buildAppointmentCalendarEvent } from '../../../../utils/appointmentCalendarEvent'
import { cancelAppointment } from '../../../../api/appointments'
import { useClinicsOptions } from '../../../../hooks/useClinicsOptions'
import { useHistory, useLocation } from 'react-router-dom'
import BackPageHeader from '../../../../components/ui/header/BackPageHeader'

const ButtonWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 360px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		margin-top: 16px;
		width: 100%;
	}
`

const EMPTY_VALUE = '-'
const CANCEL_THRESHOLD = config.cancelThresholdHours

const AppointmentsDetail = () => {
	const history = useHistory()
	const location = useLocation()
	const appointment = location?.state?.appointment || null
	const isHistory = Boolean(appointment?.estado)
	const canCancel =
		appointment?.fechaTurnoDateTime &&
		moment(appointment.fechaTurnoDateTime).diff(moment(), 'hours', true) >=
			CANCEL_THRESHOLD
	const isMobile = useIsMobile()
	const [showModal, setShowModal] = useState(false)
	const [showFeedback, setShowFeedback] = useState({ show: false, success: false })
	const [showCancelModal, setShowCancelModal] = useState(false)
	const { clinics } = useClinicsOptions()
	const calendarEvent = buildAppointmentCalendarEvent(appointment)

	const confirmCancelAppointment = async () => {
		if (!appointment || !canCancel) {
			setShowCancelModal(false)
			return
		}

		const appointmentId = appointment.idTurno
		const clinicCode = appointment.codigoEmpresa
		const [err] = await to(cancelAppointment(appointmentId, clinicCode, 1))
		if (!err) {
			setShowCancelModal(false)
			setShowFeedback({ show: true, success: true })
		} else {
			setShowCancelModal(false)
			setShowFeedback({ show: true, success: false })
		}
	}

	return (
		<>
			<CancelAppointmentConfirmModal
				isOpen={showCancelModal}
				onClose={() => setShowCancelModal(false)}
				onConfirm={confirmCancelAppointment}
				isFullHeight={isMobile}
			/>
			<AddToCalendarQuickAccessModal
				isOpen={showModal}
				onClose={() => setShowModal(false)}
				isFullHeight={isMobile}
				event={calendarEvent}
			/>
			{showFeedback.show ? (
				<Feedback
					title={
						showFeedback.success ? 'Tu turno fue cancelado con éxito' : 'Error al cancelar el turno'
					}
					bodyText={
						showFeedback.success
							? undefined
							: 'Hubo un error al cancelar el turno. Por favor, inténtelo de nuevo.'
					}
					buttonLabel={'Volver al inicio'}
					onClickButton={() => history.push('/inicio')}
					isSuccess={showFeedback.success}
					isError={!showFeedback.success}
				/>
			) : (
				<Container>
					<Content>
						<ContentWrapper>
							<BackPageHeader
								title="Detalle del turno"
								onBack={() => history.goBack()}
								hideBackOnMobile={true}
								gap="12px"
								marginBottom="24px"
							/>
							<DetailsGrid>
								<DetailItem>
									<DetailLabel>Profesional</DetailLabel>
									<DetailValue>
										{formatDetailText(appointment?.nombreProfesional, EMPTY_VALUE)}
									</DetailValue>
								</DetailItem>
								<DetailItem>
									<DetailLabel>Especialidad</DetailLabel>
									<DetailValue>
										{formatDetailText(
											appointment?.nombreEspecialidad ||
												appointment?.descripcion ||
												appointment?.especialidad,
											EMPTY_VALUE
										)}
									</DetailValue>
								</DetailItem>
								<DetailItem>
									<DetailLabel>Fecha</DetailLabel>
									<DetailValue>{formatAppointmentDate(appointment, EMPTY_VALUE)}</DetailValue>
								</DetailItem>
								<DetailItem>
									<DetailLabel>Dirección</DetailLabel>
									<DetailValue>
										{appointment?.direccion ||
											clinics.find(c => c.codigoEmpresa === appointment?.codigoEmpresa)?.direccion ||
											EMPTY_VALUE}
									</DetailValue>
								</DetailItem>
							</DetailsGrid>
						</ContentWrapper>
						<ContentWrapper>
							{!isHistory && (
								<Button
									iconLeft={<Calendar color={theme.colors.brand.blueOmint} />}
									$showBorder={false}
									$mt={'0px'}
									label={'Agregar a calendario'}
									$variant="secondary"
									size={isMobile ? 'mobile' : 'xlarge'}
									$underline={true}
									onClick={() => setShowModal(true)}
								/>
							)}
							{canCancel && (
								<div style={{ width: "360px", display: "flex", justifyContent: "center" }}>
									<Button
										$showBorder={false}
										$mt={'0px'}
										label={'Cancelar turno'}
										$variant="tertiary"
										size={isMobile ? 'mobile' : 'xlarge'}
										$underline={true}
										onClick={() => setShowCancelModal(true)}
									/>
								</div>
							)}
						</ContentWrapper>
					</Content>
				</Container>
			)}
		</>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 80vh;
	width: 80vw;
	padding: 20px;
	justify-content: space-between;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		display: flex;
		flex-direction: column;
		height: 80vh;
		width: 100%;
		padding: 16px 0px;
	}
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	
	}
`

const ContentWrapper = styled.div`
	margin-bottom: 24px;
`

const DetailsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 28px;
	padding-bottom: 24px;
	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		gap: 16px;
	}
`

const DetailItem = styled.div`
	margin-top: auto;
	gap: 8px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		margin-top: 16px;
	}
`

const DetailLabel = styled.label`
	font-size: 13px;
	font-weight: 600;
	color: #333;
	text-transform: none;
`

const DetailValue = styled.p`
	font-size: 16px;
	color: #666;
	margin: 0;
	font-weight: 400;
`

export default AppointmentsDetail
